/**
 * 招待情報取得用のhooks
 * 認証不要の公開エンドポイントを使用
 */

import { RestApiError, restClient } from "lib/rest-client";
import { useCallback, useEffect, useState } from "react";

/**
 * 公開招待情報の型
 */
export type PublicInvitation = {
  tenantName: string;
  email: string;
  role: string;
  expiresAt: string;
  status: string;
};

/**
 * 招待情報取得hookの結果
 */
export type UseInvitationResult = {
  data: PublicInvitation | null;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
};

/**
 * トークンから招待情報を取得するhook
 * @param {string} token - 招待トークン
 * @return {UseInvitationResult} 招待情報、ローディング状態、エラー
 */
export function useInvitation(token: string): UseInvitationResult {
  const [data, setData] = useState<PublicInvitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetchInvitation = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await restClient.publicGet<PublicInvitation>(
        `/public/invitations/${token}`,
      );
      setData(response);
    } catch (e) {
      if (e instanceof RestApiError) {
        setError(e);
      } else {
        setError(
          new RestApiError(
            e instanceof Error ? e.message : "Unknown error",
            500,
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchInvitation();
  }, [fetchInvitation]);

  return { data, loading, error, refetch: fetchInvitation };
}
